import { useContext } from "react";

import MUIBox from "@mui/material/Box";
import MUITable from "@mui/material/Table";
import MUITableContainer from "@mui/material/TableContainer";
import MUITableRow from "@mui/material/TableRow";
import MUITableCell from "@mui/material/TableCell";
import MUITableHead from "@mui/material/TableHead";
import MUITableBody from "@mui/material/TableBody";
import MUIPaper from "@mui/material/Paper";
import MUIChip from "@mui/material/Chip";
import MUICheckIcon from "@mui/icons-material/Check";
import MUIWidgetIcon from "@mui/icons-material/Widgets";

import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";
import Card from "../../Card";
import EmptyState from "../../EmptyState";
import Term from "../../../Utility/Crud/Term";
import ProductSubType from "../../../Utility/Crud/ProductSubType";
import Format from "../../../Utility/Format";

/**
 * Renders a Card component displaying equipment from a service ticket.
 * @returns {JSX.Element}
 */

const title = "Service Parts";
const icon = MUIWidgetIcon;

const className = "CardChartServiceParts";

export default function CardChartServiceParts() {

    const chartContext = useContext(ChartContext);
    const mainContext = useContext(MainContext);
    const order = chartContext.chart.orderActivity[chartContext.orderId];

    // Render part table
    const renderTable = () => {
        
        // Get serviceQuote for current order
        const serviceQuote = structuredClone(
            Object.values(chartContext.chart.serviceQuote || {}).find(
                serviceQuote => serviceQuote.orderId === chartContext.orderId
            )
        );
        let serviceParts = [];
        if (serviceQuote?.documentData?.length > 0) {
            serviceQuote.documentData = JSON.parse(serviceQuote.documentData);

            // Build parts array with only necessary fields
            serviceParts = serviceQuote.documentData.map(documentDatum => {
                const catalogItem = chartContext.chart.servicePartCatalog[documentDatum.ItemId] || {};
                return {
                    servicePartCatalogCategoryTermId: catalogItem.servicePartCatalogCategoryTermId,
                    name: catalogItem.description,
                    msrp: catalogItem.msrp,
                    hcpcs: catalogItem.hcpc,
                    quantity: documentDatum.Qty,
                    warranty: documentDatum.Warranty,
                    thirtyDayFit: documentDatum.ThirtyDayFit,
                    abnRequired: documentDatum.AbnRequired,
                    detail: documentDatum.Reason
                };
            });
        }

        // const orderActivityProducts = Object.values(chartContext.chart.orderActivityProducts)
        // .filter(orderActivityProduct => orderActivityProduct.orderActivityId === chartContext.orderId);
        // const products = Object.values(chartContext.chart.product || {})
        // .filter(product => orderActivityProducts.some(orderActivityProduct => orderActivityProduct.productsId === product.id));

        // This is the "Generic Parts" section. Removing at Heather's request...can readd if needed.
        // let productParts = [];
        // if (products.length > 0) {
        //     productParts = products
        //         .map(product => {
        //             return {
        //                 servicePartCatalogCategoryTermId: null,
        //                 name: ProductSubType.getDisplayName(mainContext.productSubTypesIndexed?.[product.productSubTypeId]),
        //                 hcpcs: product.hcpcs,
        //                 quantity: 1,
        //                 warranty: false,
        //                 thirtyDayFit: false,
        //                 abnRequired: false,
        //                 detail: ""
        //             };
        //         });
        // }
        // const allParts = [...serviceParts, ...productParts];

        const allParts = serviceParts;

        // Calculate labor units from all parts (not just K0739)
        const laborUnits = calculateAdditionalLaborUnits(allParts, chartContext);

        if (allParts.length === 0) {
            return <EmptyState line1="No service parts" />;
        } else {
            // Sort parts by category name, then by hcpcs, with K0739 at the bottom
            const sortedParts = [...allParts].sort((a, b) => {
                // Move K0739 to the bottom
                if (a.hcpcs === "K0739" && b.hcpcs !== "K0739") {
                    return 1;
                }
                if (a.hcpcs !== "K0739" && b.hcpcs === "K0739") {
                    return -1;
                }

                const aCategory = Term.getDisplayName(mainContext.termsIndexed[a.servicePartCatalogCategoryTermId]) || "";
                const bCategory = Term.getDisplayName(mainContext.termsIndexed[b.servicePartCatalogCategoryTermId]) || "";
                const categoryCompare = aCategory.localeCompare(bCategory, undefined, { sensitivity: "base" });
                if (categoryCompare !== 0) {
                    return categoryCompare;
                }

                // Secondary sort by hcpcs
                return (a.hcpcs || "").localeCompare(b.hcpcs || "", undefined, { sensitivity: "base" });
            });

            function checkForABNRequired(part) {
                if (
                    !order?.abnProductOther ||
                    !part.name
                ) {
                    return false;
                }
                const partName = part.name.replace(/,/g, "") + " " + (part.hcpcs ? part.hcpcs.replace(/,/g, "") : "");
                const abnArray = order.abnProductOther.split(",");
                return abnArray.includes(partName);
            }

            return (
                <MUITableContainer component={MUIPaper} elevation={0} sx={{ mt: 1 }}>
                    <MUITable size="small">
                        <MUITableHead sx={{ backgroundColor: (theme) => theme.palette.background.default }}>
                            <MUITableRow>
                                <MUITableCell sx={{ width: "120px" }}>Category</MUITableCell>
                                <MUITableCell>Description</MUITableCell>
                                <MUITableCell sx={{ width: "80px" }}>MSRP</MUITableCell>
                                <MUITableCell sx={{ width: "80px", align: "right" }}>HCPCS</MUITableCell>
                                <MUITableCell sx={{ width: "60px" }}>Qty</MUITableCell>
                                <MUITableCell sx={{ width: "60px" }}>Warranty</MUITableCell>
                                <MUITableCell sx={{ width: "60px" }}>30D</MUITableCell>
                                <MUITableCell sx={{ width: "60px" }}>ABN</MUITableCell>
                                <MUITableCell sx={{ width: "120px" }}>Detail</MUITableCell>
                            </MUITableRow>
                        </MUITableHead>
                        <MUITableBody>
                            {sortedParts.map((part, i) => (
                                <MUITableRow key={i}>
                                    <MUITableCell>
                                        <MUIChip
                                            icon={<MUIWidgetIcon fontSize="small" />}
                                            size="small"
                                            label={
                                                part.hcpcs === "K0739"
                                                    ? "Estimated Labor"
                                                    : Term.getDisplayName(mainContext.termsIndexed[part.servicePartCatalogCategoryTermId]) || "None"
                                            }
                                            sx={{
                                                height: "26px",
                                                '& .MuiChip-label': { px: 1.5 },
                                                backgroundColor: (theme) => theme.palette.background.default
                                            }}
                                        />
                                    </MUITableCell>
                                    <MUITableCell>
                                        {part.name}
                                    </MUITableCell>
                                    <MUITableCell sx={{ textAlign: "right" }}>
                                        {Format.currency(part.msrp)}
                                    </MUITableCell>
                                    <MUITableCell>
                                        {part.hcpcs}
                                    </MUITableCell>
                                    <MUITableCell>
                                        {part.hcpcs === "K0739"
                                            ? `${laborUnits * 15}m`
                                            : part.quantity}
                                    </MUITableCell>
                                    <MUITableCell align="center">
                                        {part.warranty ? <MUICheckIcon fontSize="small" sx={{ color: (theme) => theme.palette.success.main }} /> : ""}
                                    </MUITableCell>
                                    <MUITableCell align="center">
                                        {part.thirtyDayFit ? <MUICheckIcon fontSize="small" sx={{ color: (theme) => theme.palette.success.main }} /> : ""}
                                    </MUITableCell>
                                    <MUITableCell align="center">
                                        {checkForABNRequired(part) ? <MUICheckIcon fontSize="small" sx={{ color: (theme) => theme.palette.success.main }} /> : ""}
                                    </MUITableCell>
                                    <MUITableCell>
                                        {part.detail}
                                    </MUITableCell>
                                </MUITableRow>
                            ))}
                        </MUITableBody>
                    </MUITable>
                </MUITableContainer>
            );
        }
    };

    /**
     * Calculates how many units should be on the labor line item (if present)
     * by looking at all of the service parts and summing up their max billable
     * units. Return whatever number of units (0, 1, or 2) are needed to make
     * that sum reach 2.
     * @param {Array} parts - All parts for the order
     * @param {Object} chartContext
     * @returns {number}
     */
    function calculateAdditionalLaborUnits(parts, chartContext) {
        let totalMaxBillableUnits = 0;

        parts.forEach(part => {
            // Find productSubType by matching hcpcs
            const matchingProductSubType = Object.values(mainContext.productSubTypesIndexed).find(
                productSubType => productSubType.hcpcs === part.hcpcs
            );
            if (matchingProductSubType && matchingProductSubType.maxBillableLaborUnits) {
                totalMaxBillableUnits += Number(matchingProductSubType.maxBillableLaborUnits);
            }
        });

        // Return units needed to reach 2 (never negative)
        return Math.max(0, 2 - totalMaxBillableUnits);
    }

    /**
     * Renders the content of the card.
     * @returns {JSX.Element} MUIBox component for card content
     */
    const renderContent = () => (
        <MUIBox>
            {renderTable()}
        </MUIBox>
    );

    return (
        <Card
            renderContent={renderContent}
            getTitle={() => title}
            getIcon={() => icon}
        />
    );
}

CardChartServiceParts.meta = { className: className, title: title, icon: icon };

